import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useBillingExclusion } from "@/hooks/useBillingExclusion";
import { fetchAllRows } from "@/lib/fetch-all-rows";
import { getMissingConfirmDocs } from "@/lib/confirm-doc-gating";
import { Order, Customer } from "@/types/tms";

export type OrderWithCustomer = Order & { billing_party: Customer | null };

/**
 * Shared data source for the Unbillable Orders tab and the Dashboard insight
 * counts, so they cannot drift.
 *
 * Reads every delivered order and every BOL/tender image in the tenant (the
 * missing-document check has no server-side filter yet). Load evidence is
 * fetched only for orders missing a document. Empty-trailer / bobtail orders
 * are excluded via `useBillingExclusion` (rule: `src/lib/billing-exclusion.ts`).
 * Query keys match the legacy ones so the React Query cache is shared with
 * useOrdersQuery filters.
 */
export function useDeliveredOrdersDocumentation() {
  const { tenantId, tenantReady } = useTenant();

  const { data: deliveredOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ["delivered-orders-documentation", tenantId],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      // Paginated — a single response is capped at 1000 rows and this tenant
      // has thousands of delivered orders.
      return await fetchAllRows<OrderWithCustomer>(() =>
        supabase
          .from("orders")
          .select(`
            *,
            billing_party:customers(*),
            trailer_type:trailer_types(kind)
          `)
          .eq("status", "delivered")
          .eq("is_work_order", false)
          .eq("tenant_id", tenantId!)
          .order("requested_delivery_time", { ascending: false })
          .order("id", { ascending: true }),
      );
    },
  });

  const { data: bolImages, isLoading: bolLoading } = useQuery({
    queryKey: ["bol-images-for-delivered", tenantId],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      return await fetchAllRows<{ order_id: string }>(() =>
        supabase.from("order_bol_images").select("order_id").eq("tenant_id", tenantId!),
      );
    },
  });

  const { data: tenderImages, isLoading: tenderLoading } = useQuery({
    queryKey: ["tender-images-for-delivered", tenantId],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      return await fetchAllRows<{ order_id: string }>(() =>
        supabase.from("order_tender_images").select("order_id").eq("tenant_id", tenantId!),
      );
    },
  });

  const imagesReady = !!bolImages && !!tenderImages;
  const ordersWithBol = useMemo(
    () => new Set(bolImages?.map((img) => img.order_id) ?? []),
    [bolImages],
  );
  const ordersWithTender = useMemo(
    () => new Set(tenderImages?.map((img) => img.order_id) ?? []),
    [tenderImages],
  );

  const getMissingRequirements = useCallback(
    (order: OrderWithCustomer): string[] =>
      getMissingConfirmDocs({
        customer: order.billing_party,
        tenderId: order.tender_id,
        hasBolImage: ordersWithBol.has(order.id),
        hasTenderImage: ordersWithTender.has(order.id),
      }),
    [ordersWithBol, ordersWithTender],
  );

  // Candidates: orders missing a required document, before the empty-trailer
  // exclusion. Load evidence is fetched for these only.
  const candidates = useMemo(() => {
    if (!imagesReady) return [];
    return (deliveredOrders ?? []).filter((o) => getMissingRequirements(o).length > 0);
  }, [deliveredOrders, imagesReady, getMissingRequirements]);
  const candidateIds = useMemo(() => candidates.map((o) => o.id), [candidates]);

  const {
    isExcludedFromBilling,
    isLoading: exclusionLoading,
    error: exclusionError,
  } = useBillingExclusion(tenantId, candidateIds);

  const unbillableOrders = useMemo(
    () => candidates.filter((order) => !isExcludedFromBilling(order)),
    [candidates, isExcludedFromBilling],
  );

  return {
    isLoading: ordersLoading || bolLoading || tenderLoading || exclusionLoading,
    /** Set when load evidence failed to load; nothing is excluded then. */
    exclusionError,
    unbillableOrders,
    getMissingRequirements,
  };
}
